import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ShieldCheck, Eye, Camera, Upload, Loader2, Check, ArrowRight, ArrowLeft } from "lucide-react";

export const PersonaSetup = () => {
  const { personaOpen, closePersona, user, onPersonaSaved } = useAuth();
  // Never overlay the persona gate on the auth routes (it would intercept the auth form clicks).
  const _authRoute = typeof window !== "undefined" && ["/login", "/verify-email", "/reset-password"].includes(window.location.pathname);
  const [step, setStep] = useState(0);
  const [presets, setPresets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // form state
  const [confirm18, setConfirm18] = useState(false);
  const [age, setAge] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState("");
  const [role, setRole] = useState("");
  const [bio, setBio] = useState("");
  const [avatarType, setAvatarType] = useState("preset");
  const [presetId, setPresetId] = useState(null);
  const [customUrl, setCustomUrl] = useState(null);

  useEffect(() => {
    if (personaOpen) {
      setStep(0);
      setDisplayName(user?.display_name || user?.name || "");
      setBio(user?.bio || "");
      setAge(user?.age ? String(user.age) : "");
      setGender(user?.gender || "");
      setRole(user?.role || "");
      setConfirm18(!!(user?.is_adult || (user?.age && user.age >= 18)));
      if (user?.avatar_type === "custom" && user?.avatar_url) {
        setAvatarType("custom");
        setCustomUrl(user.avatar_url);
        setPresetId(null);
      }
      api.get("/avatars/presets").then((r) => {
        setPresets(r.data);
        if (user?.avatar_type === "preset" && user?.avatar_url) {
          const match = r.data.find((p) => p.url === user.avatar_url);
          if (match) { setAvatarType("preset"); setPresetId(match.id); }
        }
      }).catch(() => {});
    }
  }, [personaOpen, user]);

  const canStep0 = confirm18 && (age === "" || Number(age) >= 18);
  const canStep1 = displayName.trim() && gender && role;
  const chosenAvatar = avatarType === "preset"
    ? presets.find((p) => p.id === presetId)?.url
    : (customUrl ? resolveAvatar(customUrl) : null);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image")) { toast.error("Pick an image"); return; }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/profile/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      setCustomUrl(res.data.avatar_url);
      setAvatarType("custom");
      setPresetId(null);
      toast.success("Avatar uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (avatarType === "preset" && !presetId) { toast.error("Pick an avatar"); return; }
    if (avatarType === "custom" && !customUrl) { toast.error("Upload an avatar"); return; }
    setSaving(true);
    try {
      const payload = {
        display_name: displayName.trim(), bio: bio.trim(), age: age ? Number(age) : 18,
        gender, role, avatar_type: avatarType,
        preset_id: avatarType === "preset" ? presetId : null,
        avatar_url: avatarType === "custom" ? customUrl : null,
      };
      const res = await api.put("/profile", payload);
      onPersonaSaved(res.data);
      toast.success("Welcome to Barefootage!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const filteredPresets = gender ? presets.filter((p) => p.gender === gender) : presets;

  return (
    <Dialog open={personaOpen && !_authRoute} onOpenChange={(o) => { if (!o) closePersona(); }}>
      <DialogContent data-testid="persona-setup-dialog" className="max-w-lg bg-card border-border max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium">
            {step === 0 && "Confirm your age"}
            {step === 1 && "Create your persona"}
            {step === 2 && "Choose your avatar"}
          </DialogTitle>
          <DialogDescription>
            {step === 0 && "Barefootage is an 18+ community. Confirm before you continue."}
            {step === 1 && "This is how you'll appear across the community."}
            {step === 2 && "Every avatar is shown barefoot, in flip-flops, or in sandals — feet on show."}
          </DialogDescription>
        </DialogHeader>

        {/* step dots */}
        <div className="flex gap-1.5 mb-2">
          {[0, 1, 2].map((s) => (
            <div key={s} className={cn("h-1 flex-1 rounded-full transition-colors", s <= step ? "bg-primary" : "bg-secondary")} />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-5">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex gap-3">
              <ShieldCheck className="w-6 h-6 text-primary shrink-0" />
              <p className="text-sm text-muted-foreground">You must be at least 18 years old to view, submit, or rate content on Barefootage.</p>
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-muted-foreground">Your age <span className="normal-case tracking-normal text-muted-foreground/70">(optional)</span></label>
              <Input
                data-testid="persona-age-input"
                type="number" min="0" placeholder="Optional"
                value={age} onChange={(e) => setAge(e.target.value)}
                className="mt-1.5 bg-secondary/50 border-border focus-visible:ring-primary"
              />
              {age !== "" && Number(age) < 18 && (
                <p className="text-xs text-red-400 mt-1.5">You must be 18 or older.</p>
              )}
            </div>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <Checkbox data-testid="persona-confirm-18" checked={confirm18} onCheckedChange={(v) => setConfirm18(!!v)} />
              <span className="text-sm">I confirm that I am 18 years of age or older.</span>
            </label>
            <Button data-testid="persona-next-0" disabled={!canStep0} onClick={() => setStep(1)} className="w-full rounded-full">
              Continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <button data-testid="persona-exit" onClick={closePersona} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors">
              Maybe later — just let me look around
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-xs tracking-widest uppercase text-muted-foreground">Display name</label>
              <Input
                data-testid="persona-name-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. SandyToes" className="mt-1.5 bg-secondary/50 border-border focus-visible:ring-primary"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-muted-foreground">Gender</label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {["female", "male"].map((g) => (
                  <button key={g} data-testid={`persona-gender-${g}`} onClick={() => { setGender(g); setPresetId(null); if (g === "male") setRole("viewer"); }}
                    className={cn("rounded-lg border py-2.5 capitalize text-sm transition-colors", gender === g ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40")}>
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-muted-foreground">I'm here to…</label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button data-testid="persona-role-viewer" onClick={() => setRole("viewer")}
                  className={cn("rounded-lg border p-3 text-left transition-colors", role === "viewer" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40")}>
                  <Eye className={cn("w-5 h-5 mb-1.5", role === "viewer" ? "text-primary" : "text-muted-foreground")} />
                  <div className="text-sm font-medium">View & Rate</div>
                  <div className="text-xs text-muted-foreground">Browse, rate and comment.</div>
                </button>
                <button data-testid="persona-role-contributor" onClick={() => gender !== "male" && setRole("contributor")}
                  disabled={gender === "male"}
                  className={cn("rounded-lg border p-3 text-left transition-colors", gender === "male" ? "border-border opacity-40 cursor-not-allowed" : role === "contributor" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40")}>
                  <Camera className={cn("w-5 h-5 mb-1.5", role === "contributor" ? "text-primary" : "text-muted-foreground")} />
                  <div className="text-sm font-medium">Submit footage</div>
                  <div className="text-xs text-muted-foreground">Upload photos & videos too.</div>
                </button>
              </div>
              {gender === "male" && (
                <p className="text-xs text-muted-foreground mt-1.5">Uploading is women-only. As a guy you can view, rate, comment, DM and build your own galleries.</p>
              )}
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-muted-foreground">Short bio (optional)</label>
              <Textarea
                data-testid="persona-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} rows={2}
                placeholder="Tell the community a little about you…" className="mt-1.5 bg-secondary/50 border-border focus-visible:ring-primary resize-none"
              />
              {role === "contributor" && (
                <p className="text-xs text-muted-foreground mt-1.5">More onboarding questions for contributors are coming soon.</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(0)} className="rounded-full border-border"><ArrowLeft className="w-4 h-4" /></Button>
              <Button data-testid="persona-next-1" disabled={!canStep1} onClick={() => setStep(2)} className="flex-1 rounded-full">
                Continue <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 max-h-[24rem] overflow-y-auto pr-1 pb-1">
              {filteredPresets.map((p) => (
                <button key={p.id} data-testid={`persona-avatar-${p.id}`}
                  onClick={() => { setAvatarType("preset"); setPresetId(p.id); }}
                  className={cn("relative rounded-lg overflow-hidden border-2 aspect-square transition-all", avatarType === "preset" && presetId === p.id ? "border-primary scale-[1.03]" : "border-border hover:border-primary/40")}>
                  <img src={p.url} alt={p.style} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1 right-1 text-[9px] uppercase tracking-wide text-center py-0.5 rounded bg-black/70">{p.style}</span>
                  {avatarType === "preset" && presetId === p.id && (
                    <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center"><Check className="w-3 h-3 text-primary-foreground" /></span>
                  )}
                </button>
              ))}
            </div>

            <div className="rounded-lg border border-dashed border-border p-3">
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <div className="flex items-center gap-2 text-sm">
                  {chosenAvatar && avatarType === "custom" ? (
                    <img src={chosenAvatar} alt="custom" className="w-9 h-9 rounded-full object-cover border border-primary" />
                  ) : (
                    <Upload className="w-5 h-5 text-muted-foreground" />
                  )}
                  <span>Upload your own <span className="text-muted-foreground">(feet showing please!)</span></span>
                </div>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <span className="text-primary text-sm font-medium">Browse</span>}
                <input data-testid="persona-avatar-upload" type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
              </label>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-full border-border"><ArrowLeft className="w-4 h-4" /></Button>
              <Button data-testid="persona-save-btn" disabled={saving || !chosenAvatar} onClick={save} className="flex-1 rounded-full">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Enter Barefootage"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
